'use client';

import { useState, useEffect } from 'react';

/**
 * Custom hook for managing index-based selection with automatic bounds checking.
 * Useful for components that need to maintain a selected index that should reset
 * when the items array changes (e.g., in Puck editor scenarios).
 *
 * @param items - Array of items to select from
 * @param initialIndex - Initial selected index (defaults to 0)
 * @returns Object containing selectedIndex, setSelectedIndex, and selectedItem
 */
export function useIndexSelection<T>(items: T[], initialIndex: number = 0) {
  const [selectedIndex, setSelectedIndex] = useState(initialIndex);

  // Update selectedIndex when items array changes (e.g., in Puck editor)
  useEffect(() => {
    if (items.length > 0) {
      if (selectedIndex >= items.length) {
        setSelectedIndex(0);
      }
    }
  }, [items, selectedIndex]);

  // Get the currently selected item (with safety check)
  const selectedItem = items[selectedIndex] || null;

  return {
    selectedIndex,
    selectedItem,
    setSelectedIndex,
  };
}
